import { CheckCircle2, Info, XCircle } from "lucide-react";
import * as React from "react";

/** C25 批1(1.12,拍板③):轻量 Toast(零依赖)——操作成功/失败右上角即时反馈。
 * 用法:__root 挂 <ToastProvider>;组件内 const { toast } = useToast(); toast("已保存", "success")。 */

type ToastTone = "success" | "error" | "info";
type ToastItem = { id: number; msg: string; tone: ToastTone };

const ToastCtx = React.createContext<{ toast: (msg: string, tone?: ToastTone) => void }>({
	toast: () => undefined,
});

export function useToast() {
	return React.useContext(ToastCtx);
}

const ICON: Record<ToastTone, React.ReactNode> = {
	success: <CheckCircle2 className="h-4 w-4 text-brand-success" />,
	error: <XCircle className="h-4 w-4 text-brand-danger" />,
	info: <Info className="h-4 w-4 text-brand-info" />,
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
	const [items, setItems] = React.useState<ToastItem[]>([]);
	const idRef = React.useRef(0);
	const toast = React.useCallback((msg: string, tone: ToastTone = "success") => {
		const id = ++idRef.current;
		setItems((xs) => [...xs, { id, msg, tone }]);
		setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 3200);
	}, []);
	const ctx = React.useMemo(() => ({ toast }), [toast]);
	return (
		<ToastCtx.Provider value={ctx}>
			{children}
			<div className="pointer-events-none fixed right-4 top-4 z-[70] flex w-80 flex-col gap-2">
				{items.map((t) => (
					<output
						key={t.id}
						className="ui-toast-item pointer-events-auto flex items-start gap-2 rounded-xl border border-surface-border bg-surface-card px-3 py-2.5 text-sm text-text-primary shadow-lg"
					>
						<span className="mt-0.5 shrink-0">{ICON[t.tone]}</span>
						<span className="min-w-0 break-words">{t.msg}</span>
					</output>
				))}
			</div>
		</ToastCtx.Provider>
	);
}
